import { Controller } from "@hotwired/stimulus"
import DataTable from 'datatables.net-bs5'
import * as bootstrap from 'bootstrap'

// Connects to data-controller="admins-data-table"
export default class extends Controller {
  static targets = ['table', 'deleteModal', 'deleteAdminName', 'resetModal', 'resetModalTitle', 'resetModalMessage', 'resetAdminName', 'confirmResetBtn']
  static values = {
    accountId: String
  }
  
  connect() {
    console.log("AdminsDataTableController connected")
    this.initializeDataTable()
    this.setupEventListeners()
    this.deleteModal = new bootstrap.Modal(this.deleteModalTarget)
    this.resetModal = new bootstrap.Modal(this.resetModalTarget)
  }

  initializeDataTable() {
    this.dataTable = new DataTable(this.tableTarget, {
      'language': { 'processing': '<div id="loading"><i class="bi bi-arrow-repeat spin"></i></div>' },
      'responsive': true,
      'processing': true,
      'serverSide': true,
      'searching': false,
      'bLengthChange': false,
      'lengthMenu': 10,
      'columnDefs': [
        {
          'width': '15%',
          'name': 'created_at',
          'orderable': true,
          'className': 'text-center',
          'targets': 0
        },
        {
          'width': '25%',
          'name': 'email',
          'orderable': true,
          'className': 'text-center',
          'targets': 1
        },
        {
          'width': '20%',
          'name': 'role',
          'orderable': false,
          'className': 'text-center',
          'targets': 2
        },
        {
          'width': '15%',
          'name': 'totp_enabled',
          'orderable': false,
          'className': 'text-center',
          'targets': 3,
          'render': (data, type, row) => {
            // Check if account has TOTP enabled
            const accountTotpEnabled = this.element.dataset.accountTotpEnabled === 'true'

            if (data === 'true') {
              return '<i class="bi bi-check-circle-fill text-success"></i> Added'
            } else if (accountTotpEnabled) {
              return '<i class="bi bi-exclamation-circle-fill text-danger"></i> Action Required'
            } else {
              return '' // Don't show anything if account doesn't have TOTP enabled
            }
          }
        },
        {
          'width': '25%',
          'name': 'actions',
          'orderable': false,
          'className': 'text-center',
          'targets': 4,
          'render': (data, type, row) => {
            return this.renderActions(data, row)
          }
        }
      ],
      'createdRow': (row, data, dataIndex) => {
        if (data[3] === 'No') {
          $(row).addClass('table-danger')
        }
      },
      'ajax': {
        'url': `/accounts/${this.accountIdValue}/admins/data_sources`,
        'type': 'POST'
      }
    })
  }

  renderActions(data, row) {
    const adminEmail = row[1] || 'Unknown'
    
    return this.buildEditAction(data[0]) + 
           this.buildResetAction(data[1], adminEmail) + 
           this.buildDeleteAction(data[2], adminEmail)
  }

  buildEditAction(editUrl) {
    return `<a href="${editUrl}" class="me-2 icon-link" title="edit">
      <i class="bi bi-pencil-square text-warning"></i>
    </a>`
  }

  buildResetAction(resetUrl, adminEmail) {
    return `<a href="#" class="me-2 icon-link" data-action="click->admins-data-table#showResetModal" data-reset-url="${resetUrl}" data-admin-email="${adminEmail}" title="reset password">
      <i class="bi bi-arrow-clockwise text-danger"></i>
    </a>`
  }

  buildDeleteAction(deleteUrl, adminEmail) {
    return `<a href="#" class="me-2 icon-link" data-action="click->admins-data-table#showDeleteModal" data-delete-url="${deleteUrl}" data-admin-email="${adminEmail}" title="delete">
      <i class="bi bi-trash-fill text-danger"></i>
    </a>`
  }

  setupEventListeners() {
    // Setup delete modal
    this.setupDeleteModal()
    // Setup reset modal
    this.setupResetModal()
  }

  showResetModal(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const resetUrl = event.currentTarget.dataset.resetUrl
    const adminEmail = event.currentTarget.dataset.adminEmail

    this.resetUrl = resetUrl
    this.adminEmail = adminEmail

    // Update modal content
    this.resetModalTitleTarget.textContent = 'Reset Password'
    this.resetModalMessageTarget.textContent = 'Are you sure you want to reset password for this user?'
    this.resetAdminNameTarget.textContent = adminEmail

    // Show reset modal
    this.resetModal.show()
  }

  showDeleteModal(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const deleteUrl = event.currentTarget.dataset.deleteUrl
    const adminEmail = event.currentTarget.dataset.adminEmail
    const row = event.currentTarget.closest('tr')

    this.deleteUrl = deleteUrl
    this.rowToDelete = row

    // Show delete modal
    if (this.deleteModalTarget) {
      this.deleteAdminNameTarget.textContent = adminEmail
      
      this.deleteModal.show()
    }
  }

  setupDeleteModal() {
    // Initialize delete modal variables
    this.deleteUrl = ''
    this.rowToDelete = null
  }

  setupResetModal() {
    // Initialize reset modal variables
    this.resetUrl = ''
    this.adminEmail = ''
  }

  cancelDelete() {
    this.deleteModal.hide()
  }

  cancelReset() {
    this.resetModal.hide()
  }

  confirmDelete() {
    if (!this.deleteUrl) return
    
    this.sendRequest(this.deleteUrl, 'DELETE', this.handleDeleteResponse.bind(this), 'delete')
  }

  confirmReset() {
    if (!this.resetUrl) return
    
    this.sendRequest(this.resetUrl, 'POST', this.handleResetResponse.bind(this), 'reset')
  }

  sendRequest(url, method, responseHandler, action) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content')
    
    fetch(url, {
      method: method,
      headers: {
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        responseHandler(data)
      } else if (data.require_totp) {
        // Show TOTP modal
        this.cancelDelete()
        this.cancelReset()
        this.showTotpModal(action)
      } else {
        responseHandler(data)
      }
    })
    .catch(error => {
      console.error('Error:', error)
      alert('Operation failed')
    })
    .finally(() => {
      this.cancelDelete()
      this.cancelReset()
      this.dataTable.ajax.reload()
    })
  }

  showTotpModal(action) {
    // Use the reusable TOTP verification modal
    const event = new CustomEvent('showTotpModal', {
      detail: {
        actionUrl: action === 'delete' ? this.deleteUrl : this.resetUrl,
        actionMethod: action === 'delete' ? 'DELETE' : 'POST',
        formData: {}
      }
    })
    document.dispatchEvent(event)
  }

  handleDeleteResponse(data) {
    window.location.href = data.redirect_url
    return
    if (data.success) {
      if (this.rowToDelete) {
        this.rowToDelete.remove()
      }
    } else {
      alert(data.error || 'Delete failed')
    }
  }

  handleResetResponse(data) {
    console.log(data)
    window.location.href = data.redirect_url
    return
    if (!data.success) {
      alert(data.error || 'Password reset failed')
    }
  }

  disconnect() {
    if (this.dataTable) {
      this.dataTable.destroy()
    }
  }
} 